export interface MediaProgress {
  currentTime: number;
  duration: number;
  ratio: number;
}

type Listener = (progress: MediaProgress) => void;
const observers = new WeakMap<
  HTMLMediaElement,
  { subscribe: (listener: Listener) => () => void }
>();

export function readMediaProgress(media: HTMLMediaElement): MediaProgress {
  const duration = Number.isFinite(media.duration)
    ? Math.max(0, media.duration)
    : 0;
  const time = Number.isFinite(media.currentTime)
    ? Math.max(0, media.currentTime)
    : 0;
  const currentTime = duration > 0 ? Math.min(time, duration) : time;
  return {
    currentTime,
    duration,
    ratio: duration > 0 ? currentTime / duration : 0,
  };
}

// 数字与进度条共享同一条媒体时钟，不为每个订阅者单独开动画循环。
export function observeMediaProgress(
  media: HTMLMediaElement,
  listener: Listener,
): () => void {
  const existing = observers.get(media);
  if (existing) return existing.subscribe(listener);

  const listeners = new Set<Listener>();
  let frame: number | null = null;
  let waiting = false;
  const stop = () => {
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
  };
  const notify = (progress = readMediaProgress(media)) => {
    for (const subscriber of listeners) subscriber(progress);
  };
  const canAnimate = () =>
    listeners.size > 0 &&
    !media.paused &&
    !media.ended &&
    !media.seeking &&
    !waiting &&
    !document.hidden;
  const paint = () => {
    frame = null;
    sync();
  };
  const sync = () => {
    notify();
    if (canAnimate()) {
      if (frame === null) frame = requestAnimationFrame(paint);
    } else stop();
  };
  const resume = () => {
    waiting = false;
    sync();
  };
  const buffer = () => {
    waiting = true;
    stop();
    notify();
  };
  const reset = () => {
    waiting = true;
    stop();
    notify({ currentTime: 0, duration: 0, ratio: 0 });
  };
  const visibility = () => {
    if (document.hidden) stop();
    else sync();
  };
  const events: Record<string, EventListener> = {
    play: resume,
    playing: resume,
    pause: sync,
    ended: sync,
    seeking: sync,
    seeked: sync,
    timeupdate: sync,
    loadedmetadata: sync,
    durationchange: sync,
    ratechange: sync,
    waiting: buffer,
    error: buffer,
    loadstart: reset,
    emptied: reset,
  };
  for (const [event, handler] of Object.entries(events))
    media.addEventListener(event, handler);
  document.addEventListener('visibilitychange', visibility);

  const observer = {
    subscribe(subscriber: Listener) {
      listeners.add(subscriber);
      sync();
      return () => {
        if (!listeners.delete(subscriber)) return;
        if (listeners.size) return;
        stop();
        for (const [event, handler] of Object.entries(events))
          media.removeEventListener(event, handler);
        document.removeEventListener('visibilitychange', visibility);
        observers.delete(media);
      };
    },
  };
  observers.set(media, observer);
  return observer.subscribe(listener);
}

export function formatMediaTime(
  seconds: number,
  format: 'seconds' | 'clock' = 'seconds',
): string {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const tenths = Math.floor(safe * 10 + 1e-6);
  if (format === 'seconds') return `${(tenths / 10).toFixed(1)}s`;
  const minutes = Math.floor(tenths / 600);
  const wholeSeconds = Math.floor(tenths / 10) % 60;
  return `${minutes}:${String(wholeSeconds).padStart(2, '0')}.${tenths % 10}`;
}
